/**
 * Quas section navigation
 * The in-page links of snippets/quas-section-nav.liquid. The bar sits in
 * flow, then docks under the header once scrolled past and marks the section
 * in view. Soft routes replace the element, so it sets up on connect and
 * cleans up on disconnect.
 */
(function () {
  'use strict';

  if (customElements.get('quas-section-nav')) return;

  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class QuasSectionNav extends HTMLElement {
    connectedCallback() {
      this.cleanups = [];
      this.setup();
    }

    disconnectedCallback() {
      (this.cleanups || []).splice(0).forEach((cleanup) => cleanup());
    }

    setup() {
      const slot = this;
      const nav = slot.querySelector('.js-section-nav');
      if (!nav) return;
      const cta = nav.querySelector('.js-section-nav-cta');
      const links = Array.from(nav.querySelectorAll('.js-section-nav-link')).filter((link) => {
        const target = document.getElementById(link.hash.slice(1));
        if (!target) link.closest('li').remove();
        return Boolean(target);
      });
      if (!links.length) {
        slot.remove();
        return;
      }
      const targets = links.map((link) => document.getElementById(link.hash.slice(1)));
      const rail = nav.querySelector('.js-section-nav-rail');
      const progress = nav.querySelector('.js-section-nav-progress');

      // The lit copy: the same links in their current state, clipped down to
      // the current one. Moving the clip moves the chip and its text as one.
      const realList = rail && rail.querySelector('.quas-section-nav__links:not(.quas-section-nav__links--lit)');
      let lit = rail && rail.querySelector('.quas-section-nav__links--lit');
      if (realList && !lit) {
        realList.querySelectorAll('li').forEach((item, index) => item.style.setProperty('--i', String(index)));
        lit = realList.cloneNode(true);
        lit.classList.add('quas-section-nav__links--lit');
        lit.setAttribute('aria-hidden', 'true');
        lit.querySelectorAll('a').forEach((anchor) => {
          const label = document.createElement('span');
          label.textContent = anchor.textContent;
          anchor.replaceWith(label);
        });
        rail.appendChild(lit);
      }

      // Once the in-flow bar has passed under the header, a
      // docked tab drops from behind the header's lower edge. The header's
      // pill sets the tab's width on tablet and desktop; on phones the
      // header row is full width, so the tab is too.
      const root = document.documentElement;
      const headerPill = document.querySelector('.header__row--top .header__columns');
      const headerSection = document.querySelector('.header-section');
      const dock = { top: -1, left: 0, width: 0 };
      const measureDock = () => {
        const source = headerPill || headerSection;
        if (!source) return;
        const rect = source.getBoundingClientRect();
        const top = Math.max(0, Math.round(rect.bottom));
        const left = Math.round(rect.left);
        const width = Math.round(rect.width);
        if (top === dock.top && left === dock.left && width === dock.width) return;
        dock.top = top;
        dock.left = left;
        dock.width = width;
        nav.style.setProperty('--quas-nav-dock-top', `${dock.top}px`);
        nav.style.setProperty('--quas-nav-dock-left', `${dock.left}px`);
        nav.style.setProperty('--quas-nav-dock-width', `${dock.width}px`);
      };

      let frame = 0;
      let headerMoving = false;
      let stuck = false;
      let current = null;
      let centreTimer = 0;

      // Where a section should land: just below the docked tab. Before it
      // docks, predict its docked height from the in-flow bar, which has the
      // same content. Layout values, so the entrance does not skew them.
      const navTuck = () => parseFloat(getComputedStyle(nav).getPropertyValue('--quas-nav-tuck')) || 0;
      const anchorOffset = () => {
        const visible = stuck ? nav.offsetHeight - navTuck() : nav.offsetHeight;
        return dock.top + visible + 12;
      };

      // Chrome abandons a smooth fragment jump part way down a long page, so
      // the links scroll the page themselves; the hash still updates. Clarity
      // records the whole page again whenever the address changes, so the hash
      // follows once the scroll has settled rather than inside the tap.
      let hashTimer = 0;
      const onLinkClick = (event) => {
        const link = event.currentTarget;
        const target = document.getElementById(link.hash.slice(1));
        if (!target || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey) return;
        event.preventDefault();
        measureDock();
        const top = target.getBoundingClientRect().top + window.scrollY - anchorOffset();
        window.scrollTo({ top, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
        window.clearTimeout(hashTimer);
        hashTimer = window.setTimeout(() => {
          if (window.location.hash !== link.hash) history.pushState(null, '', link.hash);
        }, 900);
        if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      };
      links.forEach((link) => link.addEventListener('click', onLinkClick));

      // Clip the lit copy to the current link. The first placement after the
      // chip appears is instant; after that the clip glides.
      const placeChip = (instant) => {
        if (!lit || !rail) return;
        nav.classList.toggle('has-current', Boolean(current));
        if (!current) return;
        const railRect = rail.getBoundingClientRect();
        const rect = current.getBoundingClientRect();
        const left = Math.max(0, rect.left - railRect.left);
        const right = Math.max(0, railRect.right - rect.right);
        const setEdges = () => {
          lit.style.setProperty('--quas-nav-l', `${left}px`);
          lit.style.setProperty('--quas-nav-r', `${right}px`);
        };
        if (instant) {
          nav.classList.add('is-placing');
          setEdges();
          void lit.offsetWidth;
          nav.classList.remove('is-placing');
        } else {
          // Moving left, the left edge leads; the stylesheet swaps the clocks.
          const from = parseFloat(lit.style.getPropertyValue('--quas-nav-l')) || 0;
          nav.classList.toggle('is-back', left < from);
          setEdges();
        }
      };

      const centreCurrent = () => {
        const scroller = nav.querySelector('.js-section-nav-scroller');
        if (!current || !scroller || scroller.scrollWidth <= scroller.clientWidth) return;
        // Rects, not offsetLeft: each link's offsetParent is its positioned li.
        const linkRect = current.getBoundingClientRect();
        const scrollerRect = scroller.getBoundingClientRect();
        const left = scroller.scrollLeft + linkRect.left - scrollerRect.left - (scroller.clientWidth - linkRect.width) / 2;
        if (Math.abs(scroller.scrollLeft - left) < 2) return;
        scroller.scrollTo({ left, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      };

      const update = () => {
        frame = 0;
        // The header's announcement bar scrolls away before the header
        // sticks, so its lower edge is read each frame.
        measureDock();
        const slotRect = slot.getBoundingClientRect();
        // Dock as the bar reaches the header. Waiting for its bottom to
        // pass hides the first chapter's links beneath the mobile header.
        const shouldStick = slotRect.top <= dock.top;
        if (shouldStick !== stuck) {
          stuck = shouldStick;
          slot.style.minHeight = stuck ? `${slot.offsetHeight}px` : '';
          nav.classList.toggle('is-stuck', stuck);
          if (cta) cta.tabIndex = stuck ? 0 : -1;
          // Width changes with the dock, so place the chip again at once.
          placeChip(true);
        }
        const offset = anchorOffset();
        root.style.setProperty('--quas-anchor-offset', `${offset}px`);

        const line = offset + window.innerHeight * 0.25;
        let active = null;
        targets.forEach((target, index) => {
          if (target.getBoundingClientRect().top <= line) active = links[index];
        });
        if (active !== current) {
          const appearing = !current;
          if (current) current.removeAttribute('aria-current');
          if (active) active.setAttribute('aria-current', 'true');
          current = active;
          placeChip(appearing);
          // Centre the current link once the page settles. Chrome cancels a
          // running smooth page scroll (an anchor jump) when another smooth
          // scroll starts, so the strip must wait for the page.
          window.clearTimeout(centreTimer);
          if (current) centreTimer = window.setTimeout(centreCurrent, 160);
        }

        // Reading progress through the sections the nav covers.
        if (progress && stuck) {
          const first = targets[0].getBoundingClientRect().top + window.scrollY;
          const lastTarget = targets[targets.length - 1];
          const end = lastTarget.getBoundingClientRect().bottom + window.scrollY - window.innerHeight;
          const ratio = end > first ? (window.scrollY + offset - first) / (end - first + offset) : 0;
          progress.style.transform = `scaleX(${Math.min(1, Math.max(0, ratio)).toFixed(4)})`;
        }
        // A scroll gesture can finish before the header's 200ms transition.
        // Follow its lower edge until it settles so the tab stays attached.
        if (headerMoving) schedule();
      };
      const schedule = () => {
        if (!frame) frame = window.requestAnimationFrame(update);
      };

      const onResize = () => {
        measureDock();
        placeChip(true);
        schedule();
      };

      const onHeaderTransition = (event) => {
        if (event.target !== headerSection || event.propertyName !== 'transform') return;
        headerMoving = event.type === 'transitionrun';
        schedule();
      };

      headerSection?.addEventListener('transitionrun', onHeaderTransition);
      headerSection?.addEventListener('transitionend', onHeaderTransition);
      headerSection?.addEventListener('transitioncancel', onHeaderTransition);

      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', onResize);
      // First measurement in the next frame, not during start-up, so loading
      // the page does not force an extra layout pass.
      schedule();
      this.cleanups.push(() => {
        window.removeEventListener('scroll', schedule);
        window.removeEventListener('resize', onResize);
        headerSection?.removeEventListener('transitionrun', onHeaderTransition);
        headerSection?.removeEventListener('transitionend', onHeaderTransition);
        headerSection?.removeEventListener('transitioncancel', onHeaderTransition);
        if (frame) window.cancelAnimationFrame(frame);
        window.clearTimeout(centreTimer);
        window.clearTimeout(hashTimer);
        links.forEach((link) => link.removeEventListener('click', onLinkClick));
        root.style.removeProperty('--quas-anchor-offset');
      });
    }
  }

  customElements.define('quas-section-nav', QuasSectionNav);
})();
